import { FC, useCallback, useState } from "react";
import { Partition, getPartitionById, queryPartitions } from "../../services/partition-service";

import { CSSProperties } from "@mui/styles";
import NodePath from "domains/nodes/components/node-path/NodePath";
import Selector from "components/Selector";
import localisation from "../../localisation";

export type SelectedPartitionDetails = {
    id: string;
    name: string;
    partitionParentId?: number | null;
};

export type PartitionSelectedCallback = (partitionDetails: SelectedPartitionDetails | null) => void;

type PartitionSelectorProps = {
    onSelected: PartitionSelectedCallback;
    initialPartitionId?: string;
    id?: string;
    style?: CSSProperties;
    helperText?: string;
    fieldCompanyId: number;
    numberOfSuggestions?: number;
    required?: boolean;
    errorText?: string;
    isValid?: (valid: boolean) => void;
};

const getGenericPartitionTypeById = (id: string) => getPartitionById(id).then((result) => result as Partition);

const PartitionSelector: FC<PartitionSelectorProps> = (props) => {
    const [partitionParentId, setPartitionParentId] = useState<number | null>(null);
    const { onSelected, fieldCompanyId, numberOfSuggestions, required, isValid } = props;

    return (
        <>
            <Selector<string, Partition>
                id={props.id}
                style={props.style}
                initialId={props.initialPartitionId}
                search={useCallback(
                    (searchTerm) =>
                        queryPartitions({
                            fieldCompanyId: fieldCompanyId,
                            name: searchTerm,
                            reference: searchTerm,
                            ownerEmail: null,
                            itemsPerPage: numberOfSuggestions || 5,
                            pageToken: null,
                            sortOrder: "",
                        }).then((response) => response.items as Partition[]),
                    [fieldCompanyId, numberOfSuggestions]
                )}
                getById={getGenericPartitionTypeById}
                render={useCallback(
                    (partition) => `${partition.name}${!partition.reference ? "" : " (" + partition.reference + ")"}`,
                    []
                )}
                onSelected={useCallback(
                    (partition) => {
                        setPartitionParentId(
                            !partition?.parentHierarchyNodeId ? null : partition?.parentHierarchyNodeId
                        );
                        onSelected(!partition ? null : { id: partition.id, name: partition.name, partitionParentId: partition?.parentHierarchyNodeId });
                    },
                    [onSelected]
                )}
                localisation={{
                    ...localisation.selector,
                    helperText: props.helperText,
                    errorText: props.errorText,
                }}
                required={required}
                isValid={isValid}
            />
            <NodePath showAsText={true} nodeId={partitionParentId} />
        </>
    );
};

export default PartitionSelector;
